import {
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Controller } from "react-hook-form";

import { Button } from "../../../components";
import { encodeBase64String } from "../../../utils/base64";
import {
  AiInterviewEmailType,
  AiInterviewerFragment,
  useSendTestAiInterviewEmailMutation,
} from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import EmailBodyEditor from "./EmailBodyEditor/EmailBodyEditor";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

const AiInterviewerNotificationsInvitationSettings: React.FC<{
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
}> = ({ aiInterviewer, readOnly }) => {
  const { register, watch, control } = useAiInterviewerFormContext();
  const currentUser = useCurrentUser();
  const toast = useToast();

  // Get the invitation email config (always present with defaults if not customized)
  const config = aiInterviewer.currentVersion.invitationEmailConfig;

  // Available wildcards for invitation emails
  const availableWildcards = [
    { name: "candidate_name", label: "Candidate's name" },
    { name: "position_name", label: "Position name" },
    { name: "invite_expiration_days", label: "Link expiry (days)" },
  ];

  // Mutation for sending test email
  const [sendTestEmail, { loading: sendingTestEmail }] =
    useSendTestAiInterviewEmailMutation({
      onCompleted: (data) => {
        toast({
          title: "Test email sent",
          description: `Test invitation sent to ${
            data.sendTestAiInterviewEmail?.recipientEmail ?? currentUser.email
          }`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
      onError: (error) => {
        toast({
          title: "Failed to send test email",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
    });

  const handleSendTestEmail = () => {
    // Get current form values for the invitation email config
    const invitationData = watch("emailConfig.invitation");

    // Encode the body HTML to base64 for transmission
    const invitationWithEncodedHtml = {
      ...invitationData,
      bodyHtml: encodeBase64String(invitationData.bodyHtml) ?? "",
    };

    sendTestEmail({
      variables: {
        aiInterviewerId: aiInterviewer.id,
        emailType: AiInterviewEmailType.Invitation,
        emailConfig: JSON.stringify(invitationWithEncodedHtml),
      },
    });
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <HStack justifyContent="space-between" alignItems="center">
        <VStack spacing={1}>
          <AiInterviewerSectionTitle mb={0} title="Invitation email" />
          <Flex flexDirection="column">
            <Text color="gray.600" fontSize="sm">
              Customize the email sent to candidates requesting them to take an
              AI-moderated interview.
            </Text>
          </Flex>
        </VStack>
        <Button
          onClick={handleSendTestEmail}
          isLoading={sendingTestEmail}
          colorScheme="blue"
          variant="outline"
          size="sm"
        >
          Send Test Email
        </Button>
      </HStack>
      <FormControl id="fromEmailName">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email name
        </FormLabel>
        <Input
          {...register("emailConfig.invitation.fromName")}
          defaultValue={config.fromName}
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="fromEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email address
        </FormLabel>
        <Input
          {...register("emailConfig.invitation.fromEmail")}
          defaultValue={config.fromEmail}
          fontSize="sm"
          borderRadius="md"
          isDisabled
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="replyToEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Reply-to email address
        </FormLabel>
        <Input
          {...register("emailConfig.invitation.replyTo")}
          defaultValue={config.replyTo}
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailSubject">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email subject
        </FormLabel>
        <Input
          {...register("emailConfig.invitation.subject")}
          fontSize="sm"
          borderRadius="md"
          isDisabled={readOnly}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailBody">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email body
        </FormLabel>
        <Controller
          name="emailConfig.invitation.bodyHtml"
          control={control}
          defaultValue={config.bodyHtml}
          render={({ field }) => (
            <EmailBodyEditor
              value={field.value}
              fieldName={field.name}
              onChange={field.onChange}
              readOnly={readOnly}
              placeholder="Enter email body..."
              availableWildcards={availableWildcards}
            />
          )}
        />
      </FormControl>
    </VStack>
  );
};

export default AiInterviewerNotificationsInvitationSettings;
